'use client'
import React, { useCallback, useMemo, useState } from 'react'
import { Box, Button, IconButton, Snackbar, Typography } from '@mui/material'
import { useTheme } from '@mui/material/styles'
import { ContentCopy } from '@mui/icons-material'
import { TypographyVariants } from '@/app/styles/themes/typography'
import { copyToClipboard } from '@/app/lib/errors/errorUtils'

interface SupportErrorTextProps {
	supportLines?: string[]
	dataTestId?: string
}

/**
 * Renders one or more support lines (typically error details or diagnostic info)
 * with a copy-to-clipboard affordance and a transient Snackbar confirmation.
 *
 * @param props.supportLines - Optional array of strings to display and copy. Each
 *   element is rendered as a separate text line.
 * @param props.dataTestId - Optional string applied to the outer container as
 *   the `data-testid` attribute for testing.
 */
const SupportErrorText: React.FC<SupportErrorTextProps> = ({
	supportLines,
	dataTestId,
}) => {
	const theme = useTheme()
	const [snackbarOpen, setSnackbarOpen] = useState(false)
	const lines = useMemo(() => {
		if (Array.isArray(supportLines)) {
			return supportLines.filter(line => typeof line === 'string')
		}
		return []
	}, [supportLines])

	const supportText = useMemo(() => lines.join('\n'), [lines])

	const handleCopy = useCallback(async () => {
		const success = await copyToClipboard(supportText)
		if (success) {
			setSnackbarOpen(true)
		}
	}, [supportText])

	const handleCloseSnackbar = useCallback(() => {
		setSnackbarOpen(false)
	}, [])

	if (lines.length === 0) return null

	return (
		<>
			<Box
				data-testid={dataTestId}
				sx={{
					mt: 1,
					color: theme.palette.foreground.neutral.onSurfaceVar01,
					textAlign: 'center',
					display: 'flex',
					direction: 'row',
					alignItems: 'center',
					justifyContent: 'center',
				}}
			>
				<Box>
					{supportLines?.map(line => (
						<Typography
							key={line}
							variant={TypographyVariants.extraSmallRegular}
						>
							{line}
						</Typography>
					))}
				</Box>

				<IconButton
					onClick={handleCopy}
					disableRipple
					size="small"
					sx={{ pl: 2 }}
					aria-label="Copy error information to clipboard"
				>
					<ContentCopy
						fontSize="small"
						sx={{ color: theme.palette.foreground.neutral.onSurface }}
					/>
				</IconButton>
			</Box>

			<Snackbar
				open={snackbarOpen}
				autoHideDuration={3000}
				onClose={handleCloseSnackbar}
				message="Support text copied!"
				anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
				action={
					<Button color="inherit" size="small" onClick={handleCloseSnackbar}>
						Dismiss
					</Button>
				}
			/>
		</>
	)
}

export default SupportErrorText
